/* LoadingScreenWithLogic.tsx generated by @compiled/babel-plugin v0.36.1 */
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "./LoadingScreenWithLogic.compiled.css";
import * as React from 'react';
import { ax, ix } from "@compiled/react/runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { bind } from 'bind-event-listener';
import debounce from 'lodash/debounce';
import { useIntl } from 'react-intl-next';
import { findOverflowScrollParent } from '@atlaskit/editor-common/ui';
import { fg } from '@atlaskit/platform-feature-flags';
import { isMarkdownEndingWithCustomTag } from '@atlassian/ai-model-io/utils/is-markdown-with-custom-tag';
import { LoadingScreen } from '@atlassian/generative-ai-modal/screens/Loading';
import { useAIExperienceCommonDataContext } from '../../../hooks/useAIExperienceCommonData';
import { useAIUsageDisclaimer } from '../../../hooks/useAIUsageDisclaimer';
import { useLocalizedLatestPrompt } from '../../../hooks/useLocalizedLatestPrompt';
import { usePromptStatistics } from '../../../hooks/usePromptStatistics';
import { getTranslatedPromptLabel } from '../../../prebuilt/config-items/config-items';
import { useAnalyticsFlowEventsQueue } from '../../../utils/analytics/analyticsFlowUtils';
import { useFireAIAnalyticsEvent } from '../../../utils/analytics/utils';
import { UI_SCROLL_MARGIN_TOP } from '../../../utils/constants';
import { convertPMFragmentToPreview } from '../../../utils/convert-markdown-to-preview';
import { getPMFragmentWithFallback } from '../../../utils/get-pm-fragment-with-fallback';
// Selectors will be used for editorView.dom.closest() to track
// the scroll parent element and scrollable content height
var AK_EDITOR_CONTENT_AREA_SELECTOR = '.ak-editor-content-area';

/**
 * Scroll margin is used to ensure that the modal won't be
 * too close to the top of the editor, which may sometimes
 * cover the modal slightly.
 */
var scrollMarginStyles = null;
export function LoadingScreenWithLogic(_ref) {
  var parentPresetPromptLabel = _ref.parentPresetPromptLabel,
    childShortTitle = _ref.childShortTitle,
    title = _ref.title,
    latestPrompt = _ref.latestPrompt,
    promptTrigger = _ref.promptTrigger,
    refinementCount = _ref.refinementCount,
    latestPromptRetried = _ref.latestPromptRetried,
    markdown = _ref.markdown,
    idMap = _ref.idMap,
    contextStatistics = _ref.contextStatistics;
  var aiExperienceCommonData = useAIExperienceCommonDataContext();
  var configItem = aiExperienceCommonData.configItem,
    editorView = aiExperienceCommonData.editorView,
    providerFactory = aiExperienceCommonData.providerFactory,
    sendToAIExperienceMachine = aiExperienceCommonData.sendToAIExperienceMachine,
    editorPluginAIProvider = aiExperienceCommonData.editorPluginAIProvider,
    includeKnowledgeFromCurrentPage = aiExperienceCommonData.includeKnowledgeFromCurrentPage;
  var schema = editorView.state.schema;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var disclaimer = useAIUsageDisclaimer({
    editorPluginAIProvider: editorPluginAIProvider
  });
  var presetPromptLabel = getTranslatedPromptLabel(configItem, formatMessage);
  var _fireAIAnalyticsEvent = useFireAIAnalyticsEvent();
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    preview = _useState2[0],
    setPreview = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    hasPreview = _useState4[0],
    setHasPreview = _useState4[1];
  var containerRef = useRef(null);

  // raf is used for debouncing, if useEffect is called
  // multiple times before raf is run this prevents rendering
  // multiple times if we receive streams very quickly
  var raf = useRef();
  var errorReported = useRef(false);

  /**
   * scrollParentRef is for
   * storing the scroll parent element once its found
   * to be re-used in calculations
   */
  var scrollParentRef = useRef(null);
  var initialScrollTop = useRef(0);
  var modalOffsetTop = useRef(0);
  var isUserScroll = useRef(false);
  /**
   * isProgrammaticScroll is set to true initially
   * to detect any initial changes on render which affect
   * scroll position. There will be multiple scroll events
   * within a single user scroll so it will be set to false
   * accordingly within a single scroll action.
   */
  var isProgrammaticScroll = useRef(true);
  var isModalAtTop = useRef(false);
  var scrollModalAlignBottom = useCallback(function (modalEl, scrollParentEl) {
    // We set isProgrammaticScroll to true to flag it as a non-user scroll.
    isProgrammaticScroll.current = true;
    scrollParentEl.scroll({
      top: initialScrollTop.current + modalOffsetTop.current + modalEl.scrollHeight - scrollParentEl.offsetHeight,
      behavior: 'instant'
    });
  }, []);
  var scrollModalToTop = useCallback(function (scrollParentEl) {
    // We set isProgrammaticScroll to true to flag it as a non-user scroll.
    isProgrammaticScroll.current = true;
    scrollParentEl.scroll({
      top: initialScrollTop.current + modalOffsetTop.current - UI_SCROLL_MARGIN_TOP,
      behavior: 'instant'
    });
  }, []);
  useEffect(function () {
    /**
     * Set up ResizeObserver to detect cases where the editor content grows,
     * as it will normally trigger a scroll event due to scrollHeight and scrollTop changes.
     * We set isProgrammaticScroll to true to flag it as a non-user scroll.
     */
    var editorContentArea = editorView.dom.closest(AK_EDITOR_CONTENT_AREA_SELECTOR);
    if (!editorContentArea) {
      return;
    }
    var resizeObserver = new ResizeObserver(function () {
      isProgrammaticScroll.current = true;
    });
    resizeObserver.observe(editorContentArea);
    return function () {
      return resizeObserver.unobserve(editorContentArea);
    };
  }, [editorView]);

  /**
   * Editor AI can be invoked directly into this Loading screen.
   * ExperienceApplication calls scrollIntoView
   * (currently only when platform_editor_ai_scroll_into_view_fix is on, when off it is triggered from UserInputCommandPalette)
   *
   * We need to check whether scrollIntoView is complete to determine when to track user scrolling for the auto-scroll logic
   * that prevents scroll-jacking when a user scrolls away.
   */
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isScrollIntoViewSettled = _useState6[0],
    setIsScrollIntoViewSettled = _useState6[1];
  useEffect(function () {
    if (!fg('platform_editor_ai_scroll_into_view_fix')) {
      return;
    }
    var modalEl = containerRef.current;
    if (!modalEl) {
      return;
    }
    var scrollParentEl = findOverflowScrollParent(modalEl);
    if (!scrollParentEl) {
      return;
    }
    var settleScroll = debounce(function () {
      setIsScrollIntoViewSettled(true);
      /**
       * Once it's settled, we can unbind
       * as we only need to check when it first loads
       *
       * This is to emulate scrollEnd which is not supported
       * across all browsers at the moment.
       *
       * unbind taken from the bind function below
       */
      unbind();
    }, 250);
    var unbind = bind(scrollParentEl, {
      type: 'scroll',
      listener: settleScroll
    });
  }, []);
  useEffect(function () {
    /**
     * Setup init values to calculate scroll distance and
     * scroll the modal into view if it is not already visible.
     */
    var modalEl = containerRef.current;
    if (!modalEl) {
      return;
    }
    var scrollParentEl = findOverflowScrollParent(modalEl);
    if (!scrollParentEl) {
      return;
    }
    scrollParentRef.current = scrollParentEl;

    // Calculate the offset of the modal from the top of editor
    modalOffsetTop.current = modalEl.getBoundingClientRect().top - scrollParentEl.getBoundingClientRect().top;

    // Calculate initial scroll position of editor
    initialScrollTop.current = scrollParentEl.scrollTop;

    // Calculate position of the bottom of the modal from top of editor
    var modalBottomOffsetTop = modalOffsetTop.current + modalEl.scrollHeight;
    if (modalBottomOffsetTop < 0) {
      // Edge case: if modal above the top of the editor
      scrollModalToTop(scrollParentEl);
    } else if (modalOffsetTop.current > scrollParentEl.offsetHeight) {
      /**
       * Edge case: if modal is below the bottom of the editor
       * Call scrollModalAlignBottom to move modal into view.
       */
      scrollModalAlignBottom(modalEl, scrollParentEl);
    }
    var unbind = bind(scrollParentEl, {
      type: 'scroll',
      listener: function listener() {
        if (fg('platform_editor_ai_scroll_into_view_fix')) {
          if (isScrollIntoViewSettled && !isProgrammaticScroll.current) {
            isUserScroll.current = true;
            unbind();
          }
        } else {
          if (!isProgrammaticScroll.current) {
            isUserScroll.current = true;
            unbind();
          }
        }
        isProgrammaticScroll.current = false;
      }
    });
    return unbind;
  }, [scrollModalToTop, scrollModalAlignBottom, isScrollIntoViewSettled]);
  function scrollModalIntoView() {
    /**
     * Early exits:
     * - If user has manually scrolled, stop auto-scrolling
     * - If modal has reached the top of the editor, no more calculations or scrolling needed
     */
    if (isUserScroll.current || isModalAtTop.current) {
      return;
    }
    var modalEl = containerRef.current;
    if (!modalEl) {
      return;
    }
    var scrollParentEl = scrollParentRef.current;
    if (!scrollParentEl) {
      return;
    }

    // Check if modal is already fully visible
    var isModalFullyVisible = scrollParentEl.offsetHeight > modalOffsetTop.current + modalEl.scrollHeight;

    /**
     * Check if modal height exceeds scroll parent's height
     * Scroll margin is used to prevent the modal from being scrolled to the top
     * and should match scroll margin in other modals e.g. from
     * packages/editor/editor-plugin-ai/src/ui/components/FloatingContainer/styles.ts
     */
    var isModalFullHeight = scrollParentEl.offsetHeight - UI_SCROLL_MARGIN_TOP < modalEl.scrollHeight;
    if (isModalFullyVisible) {
      return;
    }
    if (isModalFullHeight) {
      scrollModalToTop(scrollParentEl);
      isModalAtTop.current = true;
      return;
    }
    scrollModalAlignBottom(modalEl, scrollParentEl);
  }
  useEffect(function () {
    // only run the callback if the raf has not been set, and then
    // reset raf to undefined
    if (raf.current === undefined) {
      raf.current = window.requestAnimationFrame(function () {
        var _preview;
        raf.current = undefined;
        if (!markdown || markdown === '') {
          // When there is no streamed preview content -- set the preview to
          // undefined.
          // This allows a nicer loading screen (rather than showing an empty paragraph).
          _preview = undefined;
        } else {
          var _getPMFragmentWithFal = getPMFragmentWithFallback({
              markdown: markdown,
              schema: schema,
              source: 'loading',
              fireAIAnalyticsEvent: function fireAIAnalyticsEvent(event) {
                if (!errorReported.current) {
                  errorReported.current = true;
                  _fireAIAnalyticsEvent(event);
                }
              },
              // Ignored via go/ees005
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              idMap: idMap
            }),
            pmFragment = _getPMFragmentWithFal.pmFragment;

          /**
           * Hold off on setting the preview if we appear to be receiving
           * a custom tag through our stream. This is to hide raw tags from
           * users until the tags are complete and can be rendered back into nodes.
           */
          if (isMarkdownEndingWithCustomTag(markdown)) {
            return;
          }
          _preview = convertPMFragmentToPreview({
            schema: schema,
            pmFragment: pmFragment,
            showTelepointer: true,
            providerFactory: providerFactory
          });
          setPreview(_preview);
          setHasPreview(true);
          window.requestAnimationFrame(function () {
            scrollModalIntoView();
          });
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markdown]);
  useEffect(function () {
    return function () {
      // if the component is unmounted before the raf callback
      // has run, we cancel it
      if (raf.current) {
        window.cancelAnimationFrame(raf.current);
      }
    };
  }, []);

  // preset prompt or base generate / interrogation
  var localizedLatestPrompt = useLocalizedLatestPrompt(latestPrompt);
  var promptStatistics = usePromptStatistics(localizedLatestPrompt);
  var analyticsFlow = useAnalyticsFlowEventsQueue({
    needUpdatedArgs: true,
    stepName: 'loading',
    attributes: {
      promptType: promptTrigger,
      latestPromptRetried: latestPromptRetried,
      refinementCount: refinementCount,
      modelIO: {
        contextStatistics: contextStatistics,
        userInputCharacterCount: promptStatistics === null || promptStatistics === void 0 ? void 0 : promptStatistics.totalCharacterCount,
        userInputNodeCounts: promptStatistics === null || promptStatistics === void 0 ? void 0 : promptStatistics.nodeCounts,
        userInputLinksCount: promptStatistics === null || promptStatistics === void 0 ? void 0 : promptStatistics.linksCount,
        userInputMentionsCount: promptStatistics === null || promptStatistics === void 0 ? void 0 : promptStatistics.mentionsCount
      },
      includeKnowledgeFromCurrentPage: includeKnowledgeFromCurrentPage
    }
  });
  var previewBecameAvailable = useRef();
  if (!previewBecameAvailable.current && hasPreview) {
    previewBecameAvailable.current = performance.now();
  }
  useEffect(function () {
    var noPreviewAvailable = performance.now();
    return function () {
      if (!previewBecameAvailable.current) {
        return;
      }
      analyticsFlow.addAttributes({
        durations: {
          timeSpentWithoutPartialPreview: previewBecameAvailable.current - noPreviewAvailable,
          timeSpentWithPartialPreview: performance.now() - previewBecameAvailable.current
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return /*#__PURE__*/React.createElement("div", {
    ref: containerRef,
    className: ax(["_j4orxy5q"])
  }, /*#__PURE__*/React.createElement(LoadingScreen, {
    parentPresetPromptLabel: parentPresetPromptLabel,
    childShortTitle: childShortTitle,
    presetPromptLabel: presetPromptLabel,
    latestPrompt: localizedLatestPrompt,
    agent: configItem === null || configItem === void 0 ? void 0 : configItem.agent,
    title: title,
    preview: preview,
    providerFactory: providerFactory,
    disclaimer: disclaimer,
    onCancel: function onCancel() {
      sendToAIExperienceMachine({
        type: 'enable discard'
      });
    },
    schema: schema
  }));
}